// Activities tab URL-state codec — FE-SCRM-024.
//
// The Activities tab piggybacks on SalesCrmPage's ``?tab=activities``
// query param. To avoid collisions with sibling panels (Pipeline owns
// renewal-board state in the same param namespace, for instance), the
// Activities filters use an ``activity_`` prefix.
//
// Defaults — status=open, sort=due_at — are NOT written to the URL.
// That keeps the no-filter URL clean and matches the producer's
// landing experience (`open due-soonest`).

import type {
  ActivityListQuery, ActivityStatus, ActivityType, DueBucket, SortKey,
} from "./types";


export interface ActivitiesUrlState {
  filters: ActivityListQuery;
  sort: SortKey;
}


const _STATUSES: ActivityStatus[] = ["open", "completed", "cancelled"];
const _DUE_BUCKETS: DueBucket[] = [
  "overdue", "today", "this_week", "upcoming",
];
const _SORTS: SortKey[] = [
  "due_at", "-due_at", "created_at", "-created_at",
];
const _TYPES: ReadonlySet<ActivityType> = new Set([
  "note", "call", "email", "meeting", "follow_up", "discovery_call",
  "census_request", "census_received", "rfp_sent", "proposals_received",
  "carrier_meeting", "proposal_delivered", "finalist_presentation",
  "bor_letter_generated", "bor_letter_sent", "decision_meeting",
  "contract_executed", "win_loss_interview",
]);


const DEFAULT_STATUS: ActivityStatus = "open";
const DEFAULT_SORT: SortKey = "due_at";


export function readActivitiesStateFromUrl(
  params: URLSearchParams,
): ActivitiesUrlState {
  const rawStatus = params.get("activity_status");
  const status = (_STATUSES as string[]).includes(rawStatus ?? "")
    ? (rawStatus as ActivityStatus)
    : DEFAULT_STATUS;

  const rawType = params.get("activity_type");
  const type = rawType && _TYPES.has(rawType as ActivityType)
    ? (rawType as ActivityType) : undefined;

  const rawDue = params.get("activity_due");
  const due = (_DUE_BUCKETS as string[]).includes(rawDue ?? "")
    ? (rawDue as DueBucket) : undefined;

  const owner = params.get("activity_owner") || undefined;

  const rawSort = params.get("activity_sort");
  const sort = (_SORTS as string[]).includes(rawSort ?? "")
    ? (rawSort as SortKey) : DEFAULT_SORT;

  const filters: ActivityListQuery = { status };
  if (type) filters.type = type;
  if (due) filters.due = due;
  if (owner) filters.owner = owner;

  return { filters, sort };
}


export function writeActivitiesStateToParams(
  state: ActivitiesUrlState,
  base: URLSearchParams,
): URLSearchParams {
  // Clone the base so we don't mutate the caller's params (it's
  // usually the live URLSearchParams from react-router).
  const out = new URLSearchParams(base);

  // Always clear our keys before writing so toggling a filter off
  // doesn't leave a stale value behind.
  for (const key of [
    "activity_status",
    "activity_type",
    "activity_due",
    "activity_owner",
    "activity_sort",
  ]) {
    out.delete(key);
  }

  if (state.filters.status && state.filters.status !== DEFAULT_STATUS) {
    out.set("activity_status", state.filters.status);
  }
  if (state.filters.type) out.set("activity_type", state.filters.type);
  if (state.filters.due) out.set("activity_due", state.filters.due);
  if (state.filters.owner) out.set("activity_owner", state.filters.owner);
  if (state.sort !== DEFAULT_SORT) {
    out.set("activity_sort", state.sort);
  }

  return out;
}
